// sketchProject — проецирование рёбер тела в эскиз.
//
// Основа работы в Fusion: чтобы новая деталь совпала с уже построенной,
// её контур не рисуют на глаз, а проецируют рёбра готового тела в эскиз и
// строят от них. Без этого крышка не сядет на корпус, а кронштейн не
// совпадёт с отверстиями.
//
// Проецируем ортогонально — вдоль нормали плоскости эскиза, как в Fusion.

import { getSketchAnchorPoints } from './anchorPoints';

/** Нормаль плоскости эскиза (единичная). */
function planeNormal(planeRef) {
    const p = planeRef?.plane;
    if (p === 'XY') return [0, 0, 1];
    if (p === 'XZ') return [0, 1, 0];
    if (p === 'YZ') return [1, 0, 0];
    if (p === 'CUSTOM' && planeRef.normal) {
        const n = planeRef.normal;
        const len = Math.hypot(n[0], n[1], n[2]) || 1;
        return [n[0] / len, n[1] / len, n[2] / len];
    }
    return [0, 0, 1];
}

/**
 * Спроецировать рёбра тела в плоскость эскиза.
 *
 * edges    — [{ a: [x,y,z], b: [x,y,z] }, ...] — концы рёбер в мире;
 * planeRef — плоскость эскиза;
 * newId    — генератор идентификаторов.
 *
 * Возвращает новые примитивы эскиза (точки и линии).
 */
export function projectEdges(edges, planeRef, newId) {
    if (!edges || edges.length === 0) return [];

    const normal = planeNormal(planeRef);
    const out = [];

    /*
     * Точки, уже добавленные в эскиз, переиспользуем: у соседних рёбер общие
     * вершины, и без этого в каждой вершине корпуса оказалось бы по три-четыре
     * точки одна на другой. Контур из таких точек не замыкается — выдавить
     * его нельзя, а на вид всё правильно.
     */
    const madePoints = new Map();   // "x_y" с округлением → id точки
    const keyOf = (p) => `${p.x.toFixed(3)}_${p.y.toFixed(3)}`;

    const addPoint = (p) => {
        const k = keyOf(p);
        const existing = madePoints.get(k);
        if (existing) return existing;
        const id = newId('p');
        madePoints.set(k, id);
        out.push({ kind: 'point', id, x: p.x, y: p.y });
        return id;
    };

    for (const e of edges) {
        if (!e || !e.a || !e.b) continue;

        /*
         * Ребро, перпендикулярное плоскости, проецируется в ТОЧКУ — линии из
         * него не выйдет. Пропускаем: иначе получилась бы линия нулевой
         * длины, на которой спотыкается решатель.
         */
        const dx = e.b[0] - e.a[0];
        const dy = e.b[1] - e.a[1];
        const dz = e.b[2] - e.a[2];
        const len = Math.hypot(dx, dy, dz);
        if (len < 1e-6) continue;
        // Косинус угла между ребром и нормалью: близко к ±1 — ребро вдоль
        // нормали, то есть смотрит «в экран».
        const cos = Math.abs(
            (dx * normal[0] + dy * normal[1] + dz * normal[2]) / len,
        );
        if (cos > 0.999) continue;

        // Переводим оба конца в координаты эскиза одной функцией — той же,
        // что используется для привязок, чтобы системы координат совпали.
        const flat = getSketchAnchorPoints({
            ...planeRef,
            points3d: [e.a, e.b],
        });
        if (flat.length !== 2) continue;
        const [p1, p2] = flat;
        // Совпавшие в проекции концы — тоже вырожденный случай.
        if (Math.hypot(p2.x - p1.x, p2.y - p1.y) < 1e-4) continue;

        out.push({
            kind: 'line',
            id: newId('l'),
            p1: addPoint(p1),
            p2: addPoint(p2),
        });
    }
    return out;
}
